import type { AxiosInstance } from 'axios'
import {
  getDeveloperPlatformOrgIdKey,
  isDeveloperRole,
  resolveDeveloperPlatformOrgSource
} from '@mysaiva/core/developer-platform'
import type { UserMeRead } from './productRedirect'

/**
 * `IntegrationPermission.DeveloperPlatformManage`, spelled out because
 * `@mysaiva/core/types` is type-only at runtime. The backend renamed it from
 * the dot to the colon form (SAIV-11622); both are accepted until every
 * environment has run that migration.
 */
const MANAGE_PERMISSIONS = ['developer_platform:manage', 'developer_platform.manage']

interface UserMeOrgs {
  orgs?: { id: string }[]
}

interface UserMeOrgDetail {
  user_role_id?: string | null
  permissions?: string[]
}

function storedOrgId(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

/** `"a, b,c"` → `['a', 'b', 'c']`; blanks dropped. */
export function parseRoleIds(value: string | undefined): string[] {
  return (value ?? '')
    .split(',')
    .map(id => id.trim())
    .filter(Boolean)
}

/**
 * Whether an org role (`user_role_id`) is a Developer role: core's
 * `isDeveloperRole` or one of the partner roles added through
 * `VITE_DEVELOPER_PLATFORM_ROLE_IDS`.
 */
export function isWorkspaceDeveloperRole(
  roleId: string | null | undefined,
  developerRoleIds: string | undefined = import.meta.env.VITE_DEVELOPER_PLATFORM_ROLE_IDS
): boolean {
  return roleId != null && (isDeveloperRole(roleId) || parseRoleIds(developerRoleIds).includes(roleId))
}

/**
 * Whether the post-login redirect should open the Developer Platform
 * (`/integrations`) instead of a product: the user's selected org of the
 * workspace's product (UPT or Compass from `product_list`, see core's
 * `resolveDeveloperPlatformOrgSource`) gives them a Developer role (core's
 * `isDeveloperRole`, matched on `user_role_id`; the ids are fixed by the
 * backend migration, so they hold in every environment) with
 * `developer_platform:manage`. `VITE_DEVELOPER_PLATFORM_ROLE_IDS`
 * (comma-separated) can add further partner roles. Org admins hold the same
 * permission but keep landing in their product.
 *
 * Mirrors the org choice of core's `useUserAccessOrg` (stored org if still
 * listed, else the first one). Any failed request falls back to the normal
 * product redirect.
 */
export async function shouldOpenDeveloperPlatform(
  apiClient: AxiosInstance,
  user: UserMeRead,
  developerRoleIds: string | undefined = import.meta.env.VITE_DEVELOPER_PLATFORM_ROLE_IDS
): Promise<boolean> {
  const product = resolveDeveloperPlatformOrgSource(user.product_list)
  if (user.is_superuser || !product) {
    return false
  }

  try {
    const { data } = await apiClient.get<UserMeOrgs>(
      `v3/users/me/${product}`,
      { params: { platform: 'web' } }
    )
    const orgs = data.orgs ?? []
    if (orgs.length === 0) return false

    const stored = storedOrgId(getDeveloperPlatformOrgIdKey(product))
    const orgId = orgs.some(org => org.id === stored) ? stored : orgs[0].id
    const { data: org } = await apiClient.get<UserMeOrgDetail>(
      `v3/users/me/${product}/${orgId}`
    )

    return (
      isWorkspaceDeveloperRole(org.user_role_id, developerRoleIds) &&
      (org.permissions ?? []).some(permission => MANAGE_PERMISSIONS.includes(permission))
    )
  } catch {
    return false
  }
}
